import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Icon, ModalTrigger, Paragraph, Text } from '@zillow/constellation-10';
import { IconLockClosedFilled } from '@zillow/constellation-icons';

import numberUtils from '../../../../utils/numberUtils';
import { useGetListingDetails } from '../../../../hooks/queries/properties';

import PremiumPurchaseModal from '../../../../../srcLegacy/shared/components/Modal/PremiumPurchaseModal';
import {
  barStyles,
  dataPointContainerStyles,
  dataPointStyles,
  iconBackgroundStyles,
  iconContainerStyles,
} from '../../../../../srcLegacy/shared/components/WizardModularListing/MonthlyRentAndSecurityDeposit/components/PremiumPriceTipContent/styles';

import PremiumPaymentGateway from '../../../PaymentGateway/PremiumPaymentGateway';

import type { FreemiumAnalyticsPricingGuidanceResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type PremiumPriceTipContentProps = {
  isActiveProductTypePremium: boolean;
  pricingGuidance: FreemiumAnalyticsPricingGuidanceResponseWebDto | null;
};

const PremiumPriceTipContent = ({
  isActiveProductTypePremium = false,
  pricingGuidance = null,
}: PremiumPriceTipContentProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [displayPremiumPurchaseModal, setDisplayPremiumPurchaseModal] = useState(false);
  const [displayPremiumPaymentGateway, setDisplayPremiumPaymentGateway] = useState(false);

  const bodyContent = isActiveProductTypePremium
    ? 'See more data for your rental market to ensure your listing is competitively priced.'
    : 'Gain access to deeper insights and analytics to help you price your rental and optimize your listing.';

  const handleLearnMoreClick = () => {
    setDisplayPremiumPurchaseModal(true);
  };

  const priceToNumber = (price: string) => Number(price.replace(/[^0-9.-]+/g, ''));

  const calculatePricePercent = (price: string, marketMin: string, marketMax: string) => {
    const priceNum = priceToNumber(price);
    const marketMinNum = priceToNumber(marketMin);
    const marketMaxNum = priceToNumber(marketMax);
    return Math.round(((priceNum - marketMinNum) / (marketMaxNum - marketMinNum)) * 100);
  };

  const renderMarketPricingGraphic = () => {
    if (isActiveProductTypePremium && pricingGuidance && pricingGuidance?.marketMedian?.price) {
      const {
        property: { price: listingPrice },
        marketMin: { price: marketMinPrice },
        marketMax: { price: marketMaxPrice },
        market25thPercentile: { price: market25thPercentilePrice },
        market75thPercentile: { price: market75thPercentilePrice },
      } = pricingGuidance;

      const isPropertyPriceInRange = listingPrice
        ? priceToNumber(listingPrice) >= priceToNumber(marketMinPrice) &&
          priceToNumber(listingPrice) <= priceToNumber(marketMaxPrice)
        : false;

      return (
        <Box
          role="img"
          aria-label={`Your rent of ${listingPrice} falls between the market min of ${marketMinPrice} and max of ${marketMaxPrice}`}
          css={{ marginBlock: '600', position: 'relative' }}
        >
          <Box css={{ display: 'flex', justifyContent: 'center', paddingBlock: '400' }}>
            <Text>
              <Text asChild textStyle="body-bold">
                <span>Your rent:</span>
              </Text>
              {listingPrice
                ? ` ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(listingPrice)))}`
                : ' Not set'}
            </Text>
          </Box>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(100, 1fr)' }}>
            <Box css={{ ...barStyles, borderColor: 'border.subtle', gridColumnStart: 1, gridColumnEnd: 100 }} />
            {/* for 5500+ price mkts: do not show if these 2 values are equal to avoid weirdness in chart */}
            {market25thPercentilePrice !== market75thPercentilePrice && (
              <Box
                css={{ ...barStyles, borderColor: 'border.accent.brand.hero' }}
                style={{
                  gridColumnStart: calculatePricePercent(market25thPercentilePrice, marketMinPrice, marketMaxPrice),
                  gridColumnEnd: calculatePricePercent(market75thPercentilePrice, marketMinPrice, marketMaxPrice),
                }}
              />
            )}
          </Box>
          {/* only plot listingPrice if it's within the market range */}
          {isPropertyPriceInRange && (
            <Box
              css={dataPointContainerStyles}
              style={{ marginLeft: `${calculatePricePercent(listingPrice, marketMinPrice, marketMaxPrice)}%` }}
            >
              <Box css={dataPointStyles} />
            </Box>
          )}
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text color="text.subtle">
              Min: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMinPrice)))}
            </Text>
            <Text color="text.subtle">
              Max: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMaxPrice)))}
            </Text>
          </Box>
        </Box>
      );
    }

    //  non premium static preview version
    return (
      <>
        <Box css={{ marginBlock: '600', position: 'relative' }}>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(100, 1fr)', filter: 'blur(1.5px)' }}>
            <Box css={{ ...barStyles, borderColor: 'border.subtle', gridColumnStart: 1, gridColumnEnd: 100 }} />
            <Box
              css={{ ...barStyles, borderColor: 'border.accent.brand.hero', gridColumnStart: 10, gridColumnEnd: 70 }}
            />
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'space-between', filter: 'blur(1.5px)' }}>
            <Text textStyle="body-sm">Min</Text>
            <Text textStyle="body-sm">Max</Text>
          </Box>
          <Box css={iconContainerStyles}>
            <Box css={iconBackgroundStyles}>
              <Text textStyle="body-sm" css={{ display: 'inline-flex', alignItems: 'center' }} asChild>
                <span>
                  {' '}
                  <Icon
                    render={<IconLockClosedFilled />}
                    css={{
                      color: 'icon.accent.brand.hero',
                      backgroundColor: 'bg.neutral',
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}
                  />{' '}
                </span>
              </Text>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <div data-test-id="PremiumPriceTipContent">
        <Paragraph>{bodyContent}</Paragraph>
        {renderMarketPricingGraphic()}
        <Box>
          {isActiveProductTypePremium && (
            <>
              <ModalTrigger multiModalTriggerId="PremiumPricingGuidanceModal">
                <Button fluid={true}>Explore data</Button>
              </ModalTrigger>
            </>
          )}
          {!isActiveProductTypePremium && (
            <Button tone="brand" emphasis="outlined" fluid onClick={handleLearnMoreClick}>
              Learn more
            </Button>
          )}
        </Box>
      </div>
      {displayPremiumPurchaseModal && (
        <PremiumPurchaseModal
          onClose={() => setDisplayPremiumPurchaseModal(false)}
          onSelectUpgrade={() => {
            setDisplayPremiumPurchaseModal(false);
            setDisplayPremiumPaymentGateway(true);
          }}
          freemiumProducts={listingDetails?.freemium?.products ?? []}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
        />
      )}
      {displayPremiumPaymentGateway && (
        <PremiumPaymentGateway
          listingAlias={listingDetails?.listingAlias ?? ''}
          isActive={listingDetails?.active ?? false}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
          freemiumData={listingDetails?.freemium ?? undefined}
          isPremiumMidfunnelPayment={true}
          displayContext="NEXT_STEP"
          onClose={() => setDisplayPremiumPaymentGateway(false)}
        />
      )}
    </>
  );
};

export default PremiumPriceTipContent;
